import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { bedApi } from '../api/bedApi'
import type { Bed } from '../types/bed'

export function useGetBeds(departmentId: string, enabled = true) {
  return useQuery({
    queryKey: ['beds', departmentId],
    queryFn: () => bedApi.getBeds(departmentId),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function useGetBed(departmentId: string, id: string, enabled = true) {
  return useQuery({
    queryKey: ['bed', departmentId, id],
    queryFn: () => bedApi.getBed(departmentId, id),
    enabled: enabled && !!id,
  })
}

export function useCreateBed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, payload }: { departmentId: string; payload: Partial<Bed> }) =>
      bedApi.createBed(departmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    },
  })
}

export function useUpdateBed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, id, payload }: { departmentId: string; id: string; payload: Partial<Bed> }) =>
      bedApi.updateBed(departmentId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
      queryClient.invalidateQueries({ queryKey: ['bed'] })
    },
  })
}

export function useDeleteBed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, id }: { departmentId: string; id: string }) => bedApi.deleteBed(departmentId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    },
  })
}

export function useAssignBed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, id, payload }: { departmentId: string; id: string; payload: Partial<Bed> }) =>
      bedApi.assignBed(departmentId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] })
      queryClient.invalidateQueries({ queryKey: ['bed'] })
      queryClient.invalidateQueries({ queryKey: ['patient-items'] })
    },
  })
}
